import type { RefObject } from 'react'
import { ChatInputElementId } from '@renderer/chat/components/chat/input/chat-input-element-id'
import type { MeetingTourTarget } from './build-meeting-tour-steps'

/**
 * The chat composer is shared panel chrome rather than part of the Meeting View,
 * so it carries its own neutral `chat-input` anchor instead of a `meeting-`
 * prefixed one. The whole composer is the target: the text field alone leaves
 * the mic and mention buttons outside the spotlight. Falling back to the field
 * keeps the step working if the composer is ever rendered without the anchor.
 *
 * Its absence is meaningful — while a notepad holds the footer slot there is no
 * composer, and the step filters itself out.
 */
export function tourTargetElement(target: MeetingTourTarget): HTMLElement | null {
  if (target === 'meeting-chat') {
    return (
      document.querySelector<HTMLElement>('[data-tour-section="chat-input"]') ??
      document.getElementById(ChatInputElementId)
    )
  }
  return document.querySelector<HTMLElement>(`[data-tour-section="${target}"]`)
}

const anchors = new Map<MeetingTourTarget, RefObject<HTMLElement | null>>()

/**
 * A `RefObject` whose `current` resolves the target's `data-tour-section`
 * element on every read. The landmarks live in three disjoint subtrees (view
 * body, floating bar, footer notepad slot), so threading real refs would mean
 * a registry spanning the whole panel; coachmark and Base UI read `.current`
 * lazily when they measure and position, so a live lookup survives the notepad
 * mounting mid-tour without any registration.
 *
 * Cached per target: the coachmark backdrop keys its measurement effect on the
 * ref's identity, and a fresh object each render would re-run it every frame.
 */
export function getTourAnchor(target: MeetingTourTarget): RefObject<HTMLElement | null> {
  let anchor = anchors.get(target)
  if (!anchor) {
    anchor = {
      get current() {
        return tourTargetElement(target)
      },
      // RefObject is structurally writable; the DOM is the source of truth.
      set current(_value: HTMLElement | null) {},
    }
    anchors.set(target, anchor)
  }
  return anchor
}
